import React, { useEffect, useState, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import api from '../services/api';
import { type Event, type TeamRoster } from '../types';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../context/NotificationContext';

type FormVals = {
    eventName: string;
    teamAName: string;
    teamBName: string;
    sportType: string;
    startTime: string; // datetime-local
    status: string;
    numberOfParts: number;
    rosterA_starters: string;
    rosterA_subs: string;
    rosterB_starters: string;
    rosterB_subs: string;
};

const FormInput = ({ name, type = 'text', label, ...props }: any) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-gray-300">{label}</label>
        <Field
            id={name} name={name} type={type}
            className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-2 px-3 text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            {...props}
        />
        <ErrorMessage name={name} component="p" className="text-red-400 text-xs italic mt-1" />
    </div>
);

const FormSelect = ({ name, label, children }: any) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-gray-300">{label}</label>
        <Field
            as="select" id={name} name={name}
            className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-2 px-3 text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        >
            {children}
        </Field>
        <ErrorMessage name={name} component="p" className="text-red-400 text-xs italic mt-1" />
    </div>
);

const FormTextArea = ({ name, label, ...props }: any) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-gray-300">{label}</label>
        <Field
            as="textarea" id={name} name={name}
            className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-2 px-3 text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            {...props}
        />
        <ErrorMessage name={name} component="p" className="text-red-400 text-xs italic mt-1" />
    </div>
);

const EditEventPageContent: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showNotification } = useNotification();

    const [loading, setLoading] = useState(true);
    const [initial, setInitial] = useState<FormVals | null>(null);

    const validationSchema = Yup.object({
        eventName: Yup.string().required(t('validation.required')),
        teamAName: Yup.string().required(t('validation.required')),
        teamBName: Yup.string()
            .required(t('validation.required'))
            .test('teams-different', t('createEvent.validation.teamsCannotBeTheSame'), function (value) {
                const { teamAName } = this.parent as { teamAName?: string };
                if (!value || !teamAName) return true;
                return value.trim().toLowerCase() !== teamAName.trim().toLowerCase();
            }),
        sportType: Yup.string().required(t('validation.required')),
        startTime: Yup.date().required(t('validation.required')),
        numberOfParts: Yup.number().min(1).max(10).required(t('validation.required')),
        status: Yup.string().required(t('validation.required')),
    });

    // --- FETCH istniejącego eventu ---
    useEffect(() => {
        if (!id) return;
        setLoading(true);
        api.get(`/Events/${id}`)
            .then(res => {
                const ev: Event = res.data;
                // przygotuj teksty składów
                const toTextarea = (r?: TeamRoster) => ({
                    starters: (r?.starters ?? []).map(p => p.name).join('\n'),
                    subs: (r?.substitutes ?? []).map(p => p.name).join('\n'),
                });
                const A = toTextarea(ev.rosterA);
                const B = toTextarea(ev.rosterB);

                // datetime-local wymaga lokalnego ISO bez sekundy-strefy
                const dt = new Date(ev.startTime);
                const pad = (n: number) => String(n).padStart(2, '0');
                const localForInput =
                    `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;

                setInitial({
                    eventName: ev.eventName,
                    teamAName: ev.teamAName,
                    teamBName: ev.teamBName,
                    sportType: (ev as any).sportType ?? (ev as any).discipline ?? 'Football',
                    startTime: localForInput,
                    status: ev.status,
                    numberOfParts: ev.numberOfParts ?? 2,
                    rosterA_starters: A.starters,
                    rosterA_subs: A.subs,
                    rosterB_starters: B.starters,
                    rosterB_subs: B.subs,
                });
            })
            .catch(err => {
                console.error('GET /Events/{id} failed:', err);
                showNotification(t('eventDetail.notFound'), 'error');
            })
            .finally(() => setLoading(false));
    }, [id, showNotification, t]);

    const handleSubmit = async (values: FormVals, { setSubmitting }: FormikHelpers<FormVals>) => {
        if (!id) return;

        const parseRoster = (text: string) =>
            text.split('\n').map(s => s.trim()).filter(Boolean).map(name => ({ name, isCaptain: false }));

        const payload = {
            eventName: values.eventName,
            teamAName: values.teamAName,
            teamBName: values.teamBName,
            sportType: values.sportType,
            startTime: values.startTime, // backend parsuje DateTime (local → ISO)
            status: values.status,
            numberOfParts: values.numberOfParts,
            rosterA: { starters: parseRoster(values.rosterA_starters), substitutes: parseRoster(values.rosterA_subs) },
            rosterB: { starters: parseRoster(values.rosterB_starters), substitutes: parseRoster(values.rosterB_subs) },
        };

        try {
            await api.put(`/Events/${id}`, payload);
            showNotification(t('createEvent.successMessage'), 'success');
            navigate('/events');
        } catch (err: any) {
            console.error('PUT /Events/{id} failed:', err);
            const msg = err.response?.data?.title || err.response?.data || t('createEvent.errorMessage');
            showNotification(msg, 'error');
            setSubmitting(false);
        }
    };

    if (loading) return <p className="text-center text-white">{t('common.loading')}</p>;
    if (!initial) return <p className="text-center text-white">{t('eventDetail.notFound')}</p>;

    return (
        <div className="max-w-4xl mx-auto bg-gray-800 p-8 rounded-lg shadow-lg">
            <h1 className="text-3xl font-bold text-white text-center mb-8">{t('eventsList.editEventTitle') || 'Edytuj wydarzenie'}</h1>

            <Formik initialValues={initial} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
                {({ isSubmitting, values }) => (
                    <Form className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormInput name="eventName" label={t('createEvent.eventNameLabel')} />
                            <FormInput name="startTime" type="datetime-local" label={t('createEvent.startTimeLabel')} />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <FormInput name="teamAName" label={t('createEvent.teamALabel')} />
                            <FormInput name="teamBName" label={t('createEvent.teamBLabel')} />
                            <FormSelect name="sportType" label={t('createEvent.sportTypeLabel')}>
                                <option value="Football">{t('sportTypes.Football')}</option>
                                <option value="Basketball">{t('sportTypes.Basketball')}</option>
                                <option value="Volleyball">{t('sportTypes.Volleyball')}</option>
                            </FormSelect>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <FormInput name="numberOfParts" type="number" label={t('createEvent.partsLabel')} />
                            <FormSelect name="status" label={t('eventDetail.changeStatus')}>
                                <option value="Scheduled">{t('statuses.Scheduled')}</option>
                                <option value="InProgress">{t('statuses.InProgress')}</option>
                                <option value="Finished">{t('statuses.Finished')}</option>
                                <option value="Canceled">{t('statuses.Canceled')}</option>
                            </FormSelect>
                            <div />
                        </div>

                        <hr className="border-gray-700 my-4" />

                        <div className="text-center">
                            <h3 className="text-xl font-semibold text-white">{t('createEvent.rostersOptional')}</h3>
                            <p className="text-sm text-gray-400">{t('createEvent.rostersHelpText')}</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-4">
                                <h4 className="text-lg font-bold text-center text-white">{values.teamAName || t('common.teamA')}</h4>
                                <FormTextArea name="rosterA_starters" label={t('createEvent.starters')} rows={6} />
                                <FormTextArea name="rosterA_subs" label={t('createEvent.substitutes')} rows={4} />
                            </div>
                            <div className="space-y-4">
                                <h4 className="text-lg font-bold text-center text-white">{values.teamBName || t('common.teamB')}</h4>
                                <FormTextArea name="rosterB_starters" label={t('createEvent.starters')} rows={6} />
                                <FormTextArea name="rosterB_subs" label={t('createEvent.substitutes')} rows={4} />
                            </div>
                        </div>

                        <div className="pt-4">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                            >
                                {isSubmitting ? t('common.loading') : (t('common.save') || 'Zapisz')}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

const EditEventPage = () => (
    <Suspense fallback={<div className="text-center p-8 text-white">Ładowanie...</div>}>
        <EditEventPageContent />
    </Suspense>
);

export default EditEventPage;
